import React, { useState, useEffect } from 'react';
import { NavBar, Radio, Button, Input, Card, Space, Popup, CheckList, Toast } from 'antd-mobile';
import { getZoneApi } from '../api';
import { getApiBaseUrl } from '../utils/apiConfig';
import './MobileAddMatch.css'; // 复用添加比赛的样式

/**
 * 移动端编辑比赛组件
 * 
 * @param {Object} props - 组件属性
 * @param {Object} props.match - 要编辑的比赛数据
 * @param {string} props.eventId - 活动ID
 * @param {string} props.zone - 区域代码
 * @param {Function} props.onComplete - 完成编辑回调
 * @param {Function} props.onCancel - 取消编辑回调
 * @returns {JSX.Element} 移动端编辑比赛组件
 */
const MobileEditMatch = ({ match, eventId, zone, onComplete, onCancel }) => {
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [team1Visible, setTeam1Visible] = useState(false);
  const [team2Visible, setTeam2Visible] = useState(false);
  
  // 初始化表单数据，预填充原比赛信息
  const [formData, setFormData] = useState(() => {
    // 解析比分
    let scoreObj = { team1Score: 0, team2Score: 0 };
    if (match.scores && typeof match.scores === 'string') {
      const scoreParts = match.scores.split('-');
      if (scoreParts.length === 2) {
        scoreObj = {
          team1Score: parseInt(scoreParts[0], 10) || 0,
          team2Score: parseInt(scoreParts[1], 10) || 0
        };
      }
    }
    
    return {
      type: match.type || 'SINGLES',
      // 从match中提取球员信息
      team1: match.teams?.team1.map(t => ({
        ...t.player,
        id: t.player._id || t.player.id
      })) || [],
      team2: match.teams?.team2.map(t => ({
        ...t.player,
        id: t.player._id || t.player.id
      })) || [],
      scores: [scoreObj],
      winner: match.winner || 'team1'
    };
  });

  // 获取球员列表
  useEffect(() => {
    const fetchPlayers = async () => {
      try {
        setLoading(true);
        const zoneApi = getZoneApi(zone);
        const response = await zoneApi.players.getAll();
        if (response.success) {
          setPlayers(response.data);
        } else {
          throw new Error(response.message || '获取球员数据失败');
        }
        setLoading(false);
      } catch (err) {
        setError('获取球员数据失败');
        setLoading(false);
        console.error('获取球员数据失败:', err);
      }
    };

    if (zone) {
      fetchPlayers();
    }
  }, [zone]);

  const handleTypeChange = (type) => {
    // 如果更改比赛类型，清空已选球员
    if (type !== formData.type) {
      setFormData(prev => ({
        ...prev,
        type,
        team1: [],
        team2: []
      }));
    }
  };

  const handleTeam1Select = (selectedIds) => {
    const maxPlayers = formData.type === 'SINGLES' ? 1 : 2;
    // 限制选择数量
    if (selectedIds.length > maxPlayers) {
      Toast.show({
        content: `${formData.type === 'SINGLES' ? '单打' : '双打'}最多选择${maxPlayers}名选手`,
        position: 'center',
      });
      return;
    }
    
    // 检查是否已在队伍2中
    const hasConflict = selectedIds.some(id => 
      formData.team2.some(player => player.id === id || player._id === id)
    );
    
    if (hasConflict) {
      Toast.show({
        content: '选手已在另一队伍中',
        position: 'center',
      });
      return;
    }

    // 确保只选择与selectedIds匹配的球员
    const selectedPlayers = players.filter(player => {
      const playerId = player.id || player._id;
      return selectedIds.includes(playerId);
    });
    
    setFormData(prev => ({
      ...prev,
      team1: selectedPlayers
    }));
  };

  const handleTeam2Select = (selectedIds) => {
    const maxPlayers = formData.type === 'SINGLES' ? 1 : 2;
    // 限制选择数量
    if (selectedIds.length > maxPlayers) {
      Toast.show({
        content: `${formData.type === 'SINGLES' ? '单打' : '双打'}最多选择${maxPlayers}名选手`,
        position: 'center',
      });
      return;
    }
    
    // 检查是否已在队伍1中
    const hasConflict = selectedIds.some(id => 
      formData.team1.some(player => player.id === id || player._id === id)
    );
    
    if (hasConflict) {
      Toast.show({
        content: '选手已在另一队伍中',
        position: 'center',
      });
      return;
    }

    // 确保只选择与selectedIds匹配的球员
    const selectedPlayers = players.filter(player => {
      const playerId = player.id || player._id;
      return selectedIds.includes(playerId);
    });
    
    setFormData(prev => ({
      ...prev,
      team2: selectedPlayers
    }));
  };

  const handleScoreChange = (team, value) => {
    const scoreKey = `${team}Score`;
    const newValue = parseInt(value, 10) || 0;
    
    setFormData(prev => ({
      ...prev,
      scores: [{ ...prev.scores[0], [scoreKey]: newValue }]
    }));
    
    // 自动设置获胜方
    const team1Score = team === 'team1' ? newValue : formData.scores[0].team1Score;
    const team2Score = team === 'team2' ? newValue : formData.scores[0].team2Score;
    
    if (team1Score !== team2Score) {
      setFormData(prev => ({
        ...prev,
        winner: team1Score > team2Score ? 'team1' : 'team2'
      }));
    }
  };

  const handleSubmit = async () => {
    try {
      // 验证选手数量
      const requiredPlayers = formData.type === 'SINGLES' ? 1 : 2;
      if (formData.team1.length < requiredPlayers || formData.team2.length < requiredPlayers) {
        Toast.show({
          content: '请选择完整的队伍',
          position: 'center',
        });
        return;
      }

      // 验证比分
      if (formData.scores[0].team1Score === 0 && formData.scores[0].team2Score === 0) {
        Toast.show({
          content: '请填写比分',
          position: 'center',
        });
        return;
      }

      // 准备提交数据
      const matchData = {
        type: formData.type,
        // 按照createMatch格式提供players字段
        players: {
          team1: formData.team1.map(player => player.id || player._id),
          team2: formData.team2.map(player => player.id || player._id)
        },
        // 同时保持teams格式以兼容replaceOne逻辑
        teams: {
          team1: formData.team1.map(player => ({
            player: player.id || player._id
          })),
          team2: formData.team2.map(player => ({
            player: player.id || player._id
          }))
        },
        scores: `${formData.scores[0].team1Score}-${formData.scores[0].team2Score}`,
        winner: formData.winner,
        eventId: eventId,
        date: new Date() // 添加当前日期
      };

      console.log('准备更新的比赛数据:', matchData);
      
      // 发送请求更新比赛
      const baseUrl = getApiBaseUrl();
      const response = await fetch(`${baseUrl}/api/${zone}/matches/${match._id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(matchData)
      });
      
      const result = await response.json();

      if (result.success) {
        Toast.show({
          icon: 'success',
          content: '更新成功',
          afterClose: () => {
            // 调用完成回调，传递更新后的比赛数据
            onComplete && onComplete(result.data);
          },
        });
      } else {
        throw new Error(result.message || '更新比赛失败');
      }
    } catch (err) {
      console.error('更新比赛失败:', err);
      Toast.show({
        icon: 'fail',
        content: err.message || '更新比赛失败',
      });
    }
  };

  if (loading) {
    return <div className="adm-dot-loading">加载中...</div>;
  }

  if (error) {
    return <div className="adm-error-block">{error}</div>;
  }

  return (
    <div className="mobile-add-match mobile-edit-match" onClick={onCancel}>
      <div className="modal-overlay" onClick={(e) => e.stopPropagation()}>
        <div className="edit-match-content" onClick={(e) => e.stopPropagation()}>
          <NavBar onBack={onCancel} style={{ flexShrink: 0 }}>编辑比赛</NavBar>
          
          <div className="mobile-add-match-content">
            {/* 比赛类型选择 */}
            <Card>
              <Radio.Group 
                value={formData.type}
                onChange={handleTypeChange}
              >
                <Space justify="around" block>
                  <Radio value="SINGLES">单打</Radio>
                  <Radio value="DOUBLES">双打</Radio>
                </Space>
              </Radio.Group>
            </Card>

            {/* 选手选择 */}
            <Card title="选择选手">
              <div className="mobile-add-match-teams">
                <div className="mobile-add-match-team">
                  <Button
                    block
                    fill="outline"
                    onClick={() => setTeam1Visible(true)}
                  >
                    {formData.team1.length > 0 ? (
                      <Space wrap>
                        {formData.team1.map(player => (
                          <span key={player.id || player._id} className="mobile-selected-player">
                            {player.name}
                            <span className="mobile-player-level">[{player.level || '新手'}]</span>
                          </span>
                        ))}
                      </Space>
                    ) : (
                      '选择队伍1选手'
                    )}
                  </Button>
                </div>
                
                <div className="mobile-add-match-team">
                  <Button
                    block
                    fill="outline"
                    onClick={() => setTeam2Visible(true)}
                  >
                    {formData.team2.length > 0 ? (
                      <Space wrap>
                        {formData.team2.map(player => (
                          <span key={player.id || player._id} className="mobile-selected-player">
                            {player.name}
                            <span className="mobile-player-level">[{player.level || '新手'}]</span>
                          </span>
                        ))}
                      </Space>
                    ) : (
                      '选择队伍2选手'
                    )}
                  </Button>
                </div>
              </div>
            </Card>

            {/* 比分记录 - 优化视觉设计 */}
            <Card 
              title={
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span>记录比分</span>
                  <span style={{ fontSize: '12px', color: '#999', fontWeight: 'normal' }}>
                    (必填)
                  </span>
                </div>
              }
              style={{ 
                border: '2px solid #f0f0f0',
                borderRadius: '12px',
                backgroundColor: '#fafafa'
              }}
            >
              <div className="mobile-add-match-score-inputs">
                <div className="mobile-add-match-score-team">
                  <div style={{ 
                    fontSize: '13px', 
                    color: '#666', 
                    marginBottom: '8px',
                    textAlign: 'center',
                    fontWeight: '500'
                  }}>
                    队伍 1
                  </div>
                  <Input
                    type="number"
                    placeholder="0"
                    value={formData.scores[0].team1Score || ''}
                    onChange={val => handleScoreChange('team1', val)}
                    onFocus={(e) => {
                      // 延迟滚动，确保输入框完全可见
                      setTimeout(() => {
                        const contentEl = e.target.closest('.mobile-add-match-content');
                        if (contentEl) {
                          const inputRect = e.target.getBoundingClientRect();
                          const contentRect = contentEl.getBoundingClientRect();
                          const scrollTop = contentEl.scrollTop;
                          const targetScroll = scrollTop + inputRect.top - contentRect.top - 100;
                          contentEl.scrollTo({ top: targetScroll, behavior: 'smooth' });
                        }
                      }, 300);
                    }}
                  />
                </div>
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  fontSize: '20px',
                  color: '#999',
                  fontWeight: 'bold',
                  paddingTop: '32px'
                }}>
                  :
                </div>
                <div className="mobile-add-match-score-team">
                  <div style={{ 
                    fontSize: '13px', 
                    color: '#666', 
                    marginBottom: '8px',
                    textAlign: 'center',
                    fontWeight: '500'
                  }}>
                    队伍 2
                  </div>
                  <Input
                    type="number"
                    placeholder="0"
                    value={formData.scores[0].team2Score || ''}
                    onChange={val => handleScoreChange('team2', val)}
                    onFocus={(e) => {
                      // 延迟滚动，确保输入框完全可见
                      setTimeout(() => {
                        const contentEl = e.target.closest('.mobile-add-match-content');
                        if (contentEl) {
                          const inputRect = e.target.getBoundingClientRect();
                          const contentRect = contentEl.getBoundingClientRect();
                          const scrollTop = contentEl.scrollTop;
                          const targetScroll = scrollTop + inputRect.top - contentRect.top - 100;
                          contentEl.scrollTo({ top: targetScroll, behavior: 'smooth' });
                        }
                      }, 300);
                    }}
                  />
                </div>
              </div>
            </Card>
          </div>
          
          <div className="mobile-add-match-actions">
            <Space block>
              <Button 
                color="default"
                size="large"
                onClick={onCancel}
                style={{ flex: 1 }}
              >
                取消
              </Button>
              <Button 
                color="primary"
                size="large"
                onClick={handleSubmit}
                style={{ flex: 2 }}
              >
                保存修改
              </Button>
            </Space>
          </div>

          {/* 队伍1选手选择弹窗 */}
          <Popup
            visible={team1Visible}
            onMaskClick={() => setTeam1Visible(false)}
            position="bottom"
            bodyStyle={{ height: '60vh' }}
          >
            <div className="mobile-player-selector">
              <div className="mobile-player-selector-header">
                <span>选择队伍1选手</span>
                <Button
                  size="small"
                  onClick={() => setTeam1Visible(false)}
                >
                  确定
                </Button>
              </div>
              <div className="mobile-player-selector-content">
                <CheckList
                  multiple={formData.type === 'DOUBLES'}
                  value={formData.team1.map(player => player.id || player._id)}
                  onChange={handleTeam1Select}
                >
                  {players.map(player => (
                    <CheckList.Item key={player.id || player._id} value={player.id || player._id}>
                      {player.name} [{player.level || '新手'}]
                    </CheckList.Item>
                  ))}
                </CheckList>
              </div>
            </div>
          </Popup>

          {/* 队伍2选手选择弹窗 */}
          <Popup
            visible={team2Visible}
            onMaskClick={() => setTeam2Visible(false)}
            position="bottom"
            bodyStyle={{ height: '60vh' }}
          >
            <div className="mobile-player-selector">
              <div className="mobile-player-selector-header">
                <span>选择队伍2选手</span>
                <Button
                  size="small"
                  onClick={() => setTeam2Visible(false)}
                >
                  确定
                </Button>
              </div>
              <div className="mobile-player-selector-content">
                <CheckList
                  multiple={formData.type === 'DOUBLES'}
                  value={formData.team2.map(player => player.id || player._id)}
                  onChange={handleTeam2Select}
                >
                  {players.map(player => (
                    <CheckList.Item key={player.id || player._id} value={player.id || player._id}>
                      {player.name} [{player.level || '新手'}]
                    </CheckList.Item>
                  ))}
                </CheckList>
              </div>
            </div>
          </Popup>
        </div>
      </div>
    </div>
  );
};

export default MobileEditMatch; 